import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of lights in the string, default 14 */
  count?: number;
  /** Array of light colours, default festive multi-colour */
  colors?: string[];
  /** Which edge to place the string on, default "top" */
  edge?: "top" | "bottom";
}

/**
 * TwinkleLights — a string of small coloured lights flickering along a banner edge.
 * Great for festive, holiday, or celebration-themed banners.
 */
const TwinkleLights: React.FC<Props> = ({
  count = 14,
  colors = ["#ffd166", "#ff4d6d", "#4cc9f0", "#06d6a0"],
  edge = "top",
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: (100 / count) * i + r(0) * 3,
      delay: r(1) * 2.5,
      color: colors[i % colors.length],
    }),
    [count, colors.join(",")]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-twinkle {
          0%, 100% { opacity: 0.35; transform: scale(0.85); }
          50%       { opacity: 1;    transform: scale(1.15); }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            [edge]: 6,
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: p.color,
            boxShadow: `0 0 6px 2px ${p.color}`,
            animation: `oot-twinkle 1.8s ease-in-out ${p.delay}s infinite`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
};

export default TwinkleLights;
